import Blog from "../../layouts/Blog";
import "../../styles/Blog.css"
import hljs from 'highlight.js';
import 'highlight.js/styles/dark.css';
import {useEffect} from "react";
import IconText from "../../components/IconText";
import LinkedText from "../../components/LinkedText";
import {faSeedling, faVial, faFeather, faFlagCheckered} from "@fortawesome/free-solid-svg-icons";
import {faBuffer} from "@fortawesome/free-brands-svg-icons";

export default function MyAgenticCodingSetup() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const rightContent = (
        <div>
            <h1>
                <IconText text={"My Agentic Coding Setup"} bgColor={true}/>
            </h1>
            <div className={"blog-abstract"}>
                <p>
                    For agentic coding tasks, I use Claude Code, Copilot CLI and Gemini CLI (yes, all of them, and yes, I ran out of tokens every time). My setup is pretty vanilla, yet, it works and saves me tons of tokens every day.
                </p>
            </div>

            <div className={"blog-body"}>
                <h1><IconText text={"Global Prompt"} icon={faFeather}/></h1>
                <p>
                    Global prompt is the prompt you define at the root CLI folder (<code>.claude</code>, <code>.gemini</code>, <code>.agents</code>, etc.) that the agent follows on every project. Since the needs for each project are different, I like to keep the global prompt simple, focused on token efficiency. So I use <LinkedText href={"https://github.com/drona23/claude-token-efficient"} text={"claude-token-efficient"}/> prompt. Add this to your global configuration file (<code>.CLAUDE.md</code>, <code>.GEMINI.md</code>, <code>.AGENTS.md</code>, or whatever that is):
                </p>
                <pre><code className={"language-markdown"}>{`## Approach 
- Think before acting. Read existing files before writing code. 
- Be concise in output but thorough in reasoning. 
- Prefer editing over rewriting whole files. 
- Do not re-read files you have already read. - Test your code before declaring done. 
- No sycophantic openers or closing fluff. 
- Keep solutions simple and direct. 
- User instructions always override this file.`}</code></pre>
                <p>
                    You can also find the other prompt files that extend this prompt (like agents, analysis profiles) in the same repository but I am happy with this one alone.
                </p>

                <h1><IconText text={"CocoIndex"} icon={faSeedling}/></h1>
                <p>
                    CocoIndex is an open source ETL framework specifically designed for AI data pipelines. It focuses on maintaining data freshness through real time incremental processing. It embeds your codebase into a vector index so we can search semantically, rather than exact strings. Great for discovering concept lives across files. More available on their <LinkedText href={"https://cocoindex.io"} text={"website"}/>.
                </p>

                <h2>Setup</h2>
                <p>
                    We start by installing the CocoIndex Code CLI. I recommend using <code>pipx</code>, since isolated installs work best for CLI tools. But <code>pip</code> is an alternative as well.
                </p>
                <pre><code className={"language-zsh"}>{`# Isolated install with pipx
pipx install "cocoindex-code[full]"

# Standard pip as alternative
pip install -U "cocoindex-code[full]"`}</code></pre>

                <p>
                    After the installation is complete, navigate to root of your codebase and initialize:
                </p>
                <pre><code className={"language-zsh"}>{`ccc init`}</code></pre>

                <p>
                    This creates a <code>.cocoindex</code> configuration folder. It will prompt you to choose an embedding model and identify the directories to index. <code>sentence-transformers</code> is a local embedding model and works well, leave <code>Snowflake/snowflake-arctic-embed-xs</code> as is. It also adds <code>/.cocoindex_code/</code> directory to <code>.gitignore</code>, which is sweet.
                </p>
                <p>
                    Once the initialization is complete, we are good to generate our semantic index.
                </p>
                <pre><code className={"language-zsh"}>{`ccc index`}</code></pre>

                <p>
                    With this command, CocoIndex Code scans your code, creates chunks using <LinkedText href={"https://en.wikipedia.org/wiki/Abstract_syntax_tree"} text={"AST"}/>, generates embeddings and stores them in a local vector database.
                </p>
                <p>
                    This is an incremental process, meaning the first run takes the longest. Subsequent runs will only process the new or edited files.
                </p>
                <p>
                    If the indexing is successful, your index stats will be displayed:
                </p>
                <pre><code className={"language-plaintext"}>{`Indexing: 91 files listed | 91 added, 0 deleted, 0 reprocessed, 0 unchanged, error: 0 

Index stats: 
  Chunks: 529 
  Files: 91 
  Languages: 
    swift: 482 chunks 
    json: 46 chunks 
    markdown: 1 chunks`}</code></pre>

                <h2>CocoIndex Code as a Skill</h2>
                <p>
                    Now we can install CocoIndex Code as an agent skill. This way we can directly reach it via the <code>/ccc</code> command during our coding sessions.
                </p>
                <pre><code className={"language-zsh"}>{`npx skills add cocoindex-io/cocoindex-code`}</code></pre>

                <p>
                    During the setup, choose the CLI coding tools to your interest. <code>skills</code> handles where to place the files.
                </p>
                <p>
                    Last step I recommend is instructing your agent to always use this skill unless told otherwise. The skill itself instructs it, but sometimes the agent fails to catch up. Add the following instruction to your configuration file (<code>CLAUDE.md</code>, <code>GEMINI.md</code>, <code>AGENTS.md</code>, or whatever it is) either globally or locally, and we are done.
                </p>
                <pre><code className={"language-plaintext"}>{`Unless specifically mentioned by the user, always use /ccc skill for searching or exploration operations.`}</code></pre>

                <h2>Indexing the Codebase on Changes with Hooks</h2>
                <blockquote>
                    <p>This is for Claude Code. But for other CLIs with hook support the process is nearly identical.</p>
                </blockquote>
                <p>
                    Unlike skills, hooks run programmatically. This means we don't leave the choice to the agent, we run the commands on certain conditions. Few examples are:
                </p>
                <table>
                    <thead>
                        <tr>
                            <th>Event</th>
                            <th>Trigger</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>SessionStart</td>
                            <td>When a session begins or resumes</td>
                        </tr>
                        <tr>
                            <td>UserPromptSubmit</td>
                            <td>When you submit a prompt, before Claude processes it</td>
                        </tr>
                        <tr>
                            <td>TaskCompleted</td>
                            <td>When a task is being marked as completed</td>
                        </tr>
                    </tbody>
                </table>
                <p>
                    The full list is available on the <LinkedText href={"https://code.claude.com/docs/en/hooks"} text={"official documentation"}/>, if you are interested.
                </p>
                <p>
                    With the deterministic design of the hooks, we can ensure that every time we make a change, the codebase gets reindexed. <code>ccc index</code> is a suitable command as a hook. We can use it on:
                </p>
                <ul>
                    <li><strong>SessionStart:</strong> Session begins</li>
                    <li><strong>PostToolUse</strong> on <strong>Write</strong>, <strong>Edit</strong>, <strong>NotebookEdit:</strong> Code files changed</li>
                </ul>
                <p>
                    Now, let's navigate to <code>~/.claude/settings.json</code> and add the configurations. We will be using <code>{"[ -d \".cocoindex_code\" ]"}</code> to check if the CocoIndex is initialized on the directory before.
                </p>
                <pre><code className={"language-json"}>{`{
  "hooks": {
    "SessionStart": [
      {
        "matcher": "",
        "hooks": [
          {
            "type": "command",
            "command": "[ -d \\".cocoindex_code\\" ] && ccc index || true",
            "timeout": 60
          }
        ]
      }
    ],
    "PostToolUse": [
      {
        "matcher": "Write|Edit|NotebookEdit",
        "hooks": [
          {
            "type": "command",
            "command": "[ -d \\".cocoindex_code\\" ] && ccc index || true",
            "timeout": 60
          }
        ]
      }
    ]
  }
}`}</code></pre>

                <h1><IconText text={"Context Mode"} icon={faBuffer}/></h1>
                <p>
                    Context Mode is an MCP server that functions as a virtualization layer for context. It executes operations in sandboxed subprocesses and returns only processed outputs to the LLM. It operates on three core mechanisms:
                </p>
                <ol>
                    <li><strong>Sandboxed Execution:</strong> Instead of reading dozens of files into the context window to process data, the agent generates a script to perform the computation. The script executes within a sandboxed runtime. The system supports 10 language runtimes, including JavaScript, Python, Shell, and Rust. The agent receives only the final standard output.</li>
                    <li><strong>Output Interception:</strong> The system integrates with native CLI hooks (<code>PreToolUse</code>, <code>PostToolUse</code>) to intercept data-heavy commands like <code>curl</code>, <code>wget</code>, or large file reads. It blocks raw data dumps and redirects these operations through proprietary tools such as <code>ctx_execute</code>, <code>ctx_fetch_and_index</code>, and <code>ctx_batch_execute</code>.</li>
                    <li><strong>Session Continuity:</strong> File edits, git operations, and task states are chunked and indexed locally in a temporary SQLite database. When the agent compacts the conversation to free space, Context Mode retrieves relevant historical context dynamically. This eliminates the need to inject raw data back into the prompt context upon session resumption.</li>
                </ol>
                <p>
                    It is an amazing tool, and I highly recommend you to check the project <LinkedText href={"https://github.com/mksglu/context-mode"} text={"repository"}/>.
                </p>

                <h2>Setup</h2>
                <p>
                    Claude Code setup is pretty straightforward, you install it via the marketplace. Open Claude Code and enter:
                </p>
                <pre><code className={"language-plaintext"}>{`/plugin marketplace add mksglu/context-mode
/plugin install context-mode@context-mode`}</code></pre>
                <p>
                    Then restart Claude Code. And that's it. We have installed Context Mode as a plugin, so it registers an MCP server that expose new tools Claude can call, such as: <code>ctx_search</code>, <code>ctx_execute</code>, etc.
                </p>
                <p>
                    The repository provides detailed installation steps for other CLIs, so if you are using something else, check <code>README.md</code>.
                </p>

                <h1><IconText text={"Testing"} icon={faVial}/></h1>
                <p>
                    Let's ask a question that needs heavy exploration on <LinkedText href={"https://repomance.com"} text={"Repomance"}/> codebase. Same exact question, but with and without the setup we created.
                    We will be using Opus 4.7 with xhigh effort.
                </p>

                <h2>With</h2>
                <blockquote>
                    <p>Search the repository for all occurrences of the word 'trending' and read the full contents of every matching file to explain their interactions.</p>
                </blockquote>
                <p>Answer with 54.8k tokens:</p>
                <pre><code className={"language-markdown"}>{`The 12 files form a self-contained "Trending" feature stack. Here's how they interact.

Entry / routing
- Repomance/ContentView.swift — defines enum ViewType { curated, trending } (defaults to .trending) and switches between SwipeView and TrendingView.

Screen scaffolding
- Repomance/Views/TrendingView.swift — the screen. Owns @StateObject TrendingRepoManager.shared, builds a SwipeCard wrapping RepositoryCard, hosts
TrendingHeaderView, falls back to TrendingEmptyStateView, and overlays TrendingBottomInfoView. Its .onAppear calls checkConnectionAndInitialize, which
either reuses cached repos or triggers loadTrendingRepos. It also presents TrendingFiltersView as a sheet and re-loads when its shouldRefresh binding
flips.
- Repomance/Views/TrendingHeaderView.swift — top bar with BrutalistDropdown (view switcher), filters/announcements/statistics/settings buttons, all driven
by @Bindings owned by TrendingView.
- Repomance/Views/BrutalistDropdown.swift — the dropdown that mutates ContentView.ViewType to switch tabs.
- Repomance/Views/TrendingFiltersView.swift — sheet that writes into TrendingRepoManager.shared.filterLanguage / filterPeriod (daily/weekly/monthly, plus a
 hardcoded availableLanguages list) and toggles onRefresh to ask TrendingView to reload.
- Repomance/Views/TrendingEmptyStateView.swift — shown when there's no current repo; "LOAD TRENDING" calls the parent's loadTrending closure.
- Repomance/Views/TrendingBottomInfoView.swift — bottom HUD showing remainingCount, selected period/language and swipe-driven toast, with offset animated
by swipeProgress.
- Repomance/Views/RepositoryCard.swift — generic card used by both curated and trending paths; receives a Repository, languages, README.

State / data layer
- Repomance/TrendingRepoManager.swift — singleton ObservableObject. Holds [EnrichedTrendingRepo], filterLanguage, filterPeriod, currentIndex, exposes
hasRepos / remainingCount. Caches to UserDefaults under RepomanceTrendingCache (1-hour expiry) and calls CustomAPIService.shared for fetches.
- Repomance/CustomAPIService.swift — network. fetchTrendingUninteracted hits the custom backend repos/trending/uninteracted/ with username, batch_size,
period, optional language (used by TrendingRepoManager). A second fetchTrendingRepositories queries GitHub's search/repositories with created:>DATE
[language:LANG]. Also defines enum TrendingPeriod { daily/weekly/monthly } with daysAgo.

Models
- Repomance/Models/GitHubTrendingRepository.swift — DTOs: GitHubTrendingRepository, TrendingContributor, and EnrichedTrendingRepo (wraps the trending
payload with githubId for interaction tracking).
- Repomance/Models/Repository.swift — init(from trending: EnrichedTrendingRepo) adapts the trending DTO into the shared Repository model so RepositoryCard
renders trending repos identically to curated ones; critically uses trending.githubId as the id for interaction tracking.

Flow: ContentView → TrendingView → TrendingRepoManager.fetchTrending(username:) → CustomAPIService.fetchTrendingUninteracted → caches
[EnrichedTrendingRepo] → TrendingView maps current item via Repository(from:) → SwipeCard/RepositoryCard displays it → swipes advance currentIndex
(left=dismiss, right=star), reflected in TrendingBottomInfoView.remainingCount. Filters round-trip through TrendingFiltersView → manager state → onRefresh
→ reload.`}</code></pre>

                <h2>Without</h2>
                <blockquote>
                    <p>Search the repository for all occurrences of the word 'trending' and read the full contents of every matching file to explain their interactions.</p>
                </blockquote>
                <p>Answer with 82.9k tokens:</p>
                <pre><code className={"language-markdown"}>{`Below is a map of how the 12 "trending"-touching files fit together. I'll group by role and trace the runtime flow.

## Entry point & view switching
* **ContentView.swift**: Holds selectedView: ViewType with cases .curated and .trending, defaulting to .trending. When authenticated, renders TrendingView(selectedView: $selectedView) (or SwipeView for curated). Passes the binding down so child views can toggle modes.
* **Views/BrutalistDropdown.swift**: A reusable dropdown that mutates the same ContentView.ViewType binding (.trending / .curated). Embedded in TrendingHeaderView, it's the user-facing switch between the two app sections.

## Trending screen composition
* **Views/TrendingView.swift**: The orchestrator screen. It wires together: @StateObject TrendingRepoManager.shared (state/cache), CustomAPIService.shared (network), GitHubAuthManager (username + token + star/follow actions), NetworkMonitor, AnnouncementManager, persistent @AppStorage flags (rizz sound, haptics, followOnStar).
* **Child views composed inside TrendingView**:
    * **TrendingHeaderView**: The top bar. Receives selectedView binding plus hasActiveFilters and hasUnreadAnnouncements. Triggers four sheet bindings: showFilters, showSettings, showAnnouncements, showStatistics. Embeds BrutalistDropdown.
    * **TrendingEmptyStateView**: Shown when no currentRepository. Observes TrendingRepoManager.shared directly to display the active period/language filters, plus a LOAD TRENDING button that calls back into TrendingView.loadTrendingRepos.
    * **RepositoryCard**: Generic card used by both curated and trending. Branches on repository.category == nil to render trending differently (single-language colored label vs. full language distribution bar).
    * **TrendingBottomInfoView**: The bottom strip; flips between filter info (period + language) and a toast (e.g. "Passed", "Starred", "Followed"). Moves down as swipeProgress increases.
    * **TrendingFiltersView**: Sheet presented from the header. Mutates TrendingRepoManager.shared.filterLanguage / filterPeriod directly and uses an @Binding onRefresh to signal TrendingView to call loadTrendingRepos on dismiss.

## State / cache layer
* **TrendingRepoManager.swift** (singleton, ObservableObject): Owns @Published trendingRepos: [EnrichedTrendingRepo], filterLanguage, filterPeriod, and a private currentIndex. fetchTrending(username:githubToken:) calls CustomAPIService.fetchUninteractedTrendingRepos, maps TrendingRepoAPIResponse → GitHubTrendingRepository → EnrichedTrendingRepo, resets index, and persists to UserDefaults under key RepomanceTrendingCache. Persists a CachedTrendingBatch (1-hour TTL, filter-keyed). loadCacheFromStorage runs on init; mismatched filters or expired cache → clearCache. getNextRepo, moveToNextRepo, removeCurrentRepo (used after successful Pass/Star), refilterCachedRepos (cheap validation — trusts backend filtering), updateFilters (clears cache if filters changed). TrendingView calls these in this order: on onAppear → checkConnectionAndInitialize → either refilterCachedRepos + loadFromCache, or loadTrendingRepos → after each swipe → removeCurrentRepo → loadCurrentRepository → loadFromCache.

## Network layer
* **CustomAPIService.swift**: All HTTP. Trending-specific endpoints:
    * **fetchUninteractedTrendingRepos(username:batchSize:language:period:)**: GET {base}repos/trending/uninteracted/ returning UninteractedTrendingReposResponse. This is the primary trending feed.
    * **saveTrendingRepo(...)**: POST {base}repos/trending/save/. Called by TrendingView.loadFromCache before display so the backend Repository table has the row that interactions FK into.
    * **fetchTrendingRepositories(language:since:token:)**: Older path that hits GitHub's search API; no longer used by the primary flow.
    * **Supporting**: fetchGitHubRepoDetails, fetchGitHubReadme, recordInteraction, recordFollow, updateFollowOnStarSetting.
* **TrendingPeriod enum**: Defined here (.daily, .weekly, .monthly). Used by TrendingRepoManager, TrendingFiltersView, TrendingBottomInfoView, and TrendingEmptyStateView.

## Models
* **Models/GitHubTrendingRepository.swift**:
    * **GitHubTrendingRepository**: author, name, url, description, language, stars, forks, etc.
    * **EnrichedTrendingRepo**: Wraps GitHubTrendingRepository with a concrete githubId: Int and fetchedAt: Date. This is what TrendingRepoManager actually stores.
* **Models/Repository.swift**: Generic Repository model rendered by RepositoryCard. Has two initializers: init(from: APIRepository) for curated and init(from: EnrichedTrendingRepo) for trending — the comment marks self.id = trending.githubId as CRITICAL. Sets category = nil for trending entries to trigger the specific UI layout in RepositoryCard.

## End-to-end flow
1. App launches → ContentView shows TrendingView.
2. TrendingView.onAppear → checkConnectionAndInitialize → if TrendingRepoManager.shared.hasRepos, validate cache; else loadTrendingRepos.
3. loadTrendingRepos → TrendingRepoManager.fetchTrending → CustomAPIService.fetchUninteractedTrendingRepos → response mapped into EnrichedTrendingRepos, saved to UserDefaults.
4. loadFromCache → getNextRepo → apiService.saveTrendingRepo → builds Repository(from: enriched) → fetches README → RepositoryCard renders.
5. Swipe right → authManager.starRepository → on success: optional follow + apiService.recordFollow, then apiService.recordInteraction("Star") → removeCurrentRepo + loadCurrentRepository. TrendingBottomInfoView shows toast.
6. Swipe left → apiService.recordInteraction("Pass") → same removal/advance flow.
7. Filter sheet (TrendingFiltersView) mutates TrendingRepoManager filters, then sets onRefresh = true, triggering loadTrendingRepos in the parent.
8. Header dropdown (BrutalistDropdown) flipping selectedView to .curated swaps TrendingView out for SwipeView.`}</code></pre>

                <h1><IconText text={"Result"} icon={faFlagCheckered}/></h1>
                <p>
                    Let's calculate how much tokens we saved. Claude Code itself has <code>20.5k</code> system token usage. Yes, Claude Code has an enormous system prompt.
                </p>
                <p>
                    Without the setup, the operation cost is <code>82.9k - 20.5k = 62.4k</code> tokens.
                </p>
                <p>
                    With the setup, the operation cost is <code>54.8k - 20.5k = 34.3k</code> tokens.
                </p>
                <p>
                    This means we saved around <strong>~55%</strong>. Not bad, huh?
                </p>
                <p>
                    There are many different CLI coding assistants out there, and managing them separately can get frustrating easily. What I recommend here is having a foundational directory and creating <code>symlink</code>s from there. There is a tool called <LinkedText href={"https://ccswitch.io/en/"} text={"CC Switch"}/> that does exactly that. There might be a case where you use a specific CLI for a specific task, but project wise configurations are present for a reason.
                </p>
            </div>
        </div>
    )

    return (
        <div className="my-agentic-coding-setup-page">
            <Blog rightContent={rightContent}/>
        </div>
    )
}

